import React, { useEffect, useState, useContext } from 'react';

import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import moment from 'moment';

import { renderBnplLayout } from '../components/Bnpl/BnplLayout';
import { Breadcrumb, BreadcrumbItem } from '../components/Breadcrumb';
import LoanBanner from '../components/LoanBanner';
import LoanPaymentHistory from '../components/LoanPaymentHistory';
import Layout from '../components/common/Layout';
import TenantConfig from '../config';
import configData from '../config';

/** context */
import { UserContext } from '../context/UserContext';
import { useLoanDetails } from '../hooks/useLoanDetails';
import { getLoanPaymentHistory, getPaymentInstrumentByLoan } from '../service/loan';

const LoanDetails = ({ data }) => {
  if (!data) {
    return null;
  }
  const rowData = [
    ['Account Number', `#${data.acctrefno}`, data.acctrefno],
    ['Open Date', moment(data.openDate).format('MMMM D, YYYY'), data.openDate],
    ['Maturity Date', moment(data.maturityDate).format('MMMM D, YYYY'), data.maturityDate],
    ['Current APR', `${data.currentInterestRate}%`, data.currentInterestRate],
    [
      'Original Loan Amount',
      data.currentNoteAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      data.currentNoteAmount,
    ],
    ['Loan Term', `${data.termUnit} Months`, data.termUnit],
    [
      'Balance Due',
      data?.nextPaymentDue?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      data.nextPaymentDue,
    ],
    [
      'Principal Paid',
      (data.currentNoteAmount - data.currentPrincipalBalance).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      data.currentNoteAmount && data.currentPrincipalBalance,
    ],
  ].filter((item) => !!item[2]);

  const isBnpl = configData.getTenant() === 'lightrx';

  return (
    <div className={`loanDetails ${isBnpl && 'lightrx'}`}>
      <div>
        <div className="loanDetailsTitle">Loan Details</div>
      </div>
      <div>
        <div className="table">
          {rowData.map(([label, value]) => (
            <div className="row tableRow" key={label + value}>
              <div>
                <div className="detailLabel">{label}</div>
              </div>
              <div>
                <div className="detailValue">{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LoanDetailsPage = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(true);
  const [paymentInstrument, setPaymentInstrument] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  const { setLoading } = useContext(UserContext);
  const { loan, loanLoading } = useLoanDetails();
  const allLoadingCompleted = !loanLoading && !paymentHistoryLoading;
  const isBnpl = configData.getTenant() === 'lightrx';

  useEffect(() => {
    allLoadingCompleted ? setLoading(false) : setLoading(true);
  }, [allLoadingCompleted, setLoading]);

  useEffect(() => {
    const loadPaymentHistory = async (id) => {
      try {
        const { data } = await getLoanPaymentHistory(id);

        setPaymentHistory(data);
      } catch (e) {
        console.error(e);
      } finally {
        setPaymentHistoryLoading(false);
      }
    };

    const loadPaymentInstrument = async (id) => {
      try {
        const { data } = await getPaymentInstrumentByLoan(id);
        setPaymentInstrument(data);
      } catch (e) {
        console.log(e);
      }
    };

    loadPaymentHistory(params.id);
    loadPaymentInstrument(params.id);
  }, [params.id]);

  const navigationFn = () => {
    localStorage.setItem('prevPath', location.pathname);
    navigate('managePayment/addCard');
  };

  const addPaymentMethod = () => {
    if (TenantConfig.dashboard.showPaymentMethod) {
      return (
        <div className="addPaymentMethod">
          <div>Payment Method</div>
          <div>
            {paymentInstrument?.length ? (
              <span>
                Account Ending In {paymentInstrument[0].value?.maskedPaymentIdentifier} -{' '}
                <a onClick={navigationFn} href="">
                  Use a different one
                </a>
              </span>
            ) : (
              <span>
                None -{' '}
                <a onClick={navigationFn} href="#">
                  Add a payment method
                </a>
              </span>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return renderBnplLayout(
    <Layout>
      {allLoadingCompleted && (
        <div className="loanSection ant-layout">
          <div className={`loanContainer ${isBnpl && 'lightrx'}`}>
            <div className="block breadcrumb">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/dashboard">Dashboard</Link>
                </BreadcrumbItem>
                {loan && <BreadcrumbItem>Loan #{loan?.loanDetail?.acctrefno}</BreadcrumbItem>}
              </Breadcrumb>
            </div>
            <LoanBanner paymentInstrument={paymentInstrument} loan={loan} data={loan?.loanDetail} />
            <div>
              <LoanDetails loading={false} data={loan?.loanDetail} />
              {addPaymentMethod()}
              <LoanPaymentHistory data={paymentHistory} />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default LoanDetailsPage;
